/**
 * DOG WIZARD - Schema-driven wizard for adding dogs to a kennel
 * Entity Type: PROFILE
 * 5 Steps: Podstawy → Pochodzenie → Zdrowie → Osiągnięcia → Zdjęcia
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { WizardStep, WizardActions } from '@/components/WizardShell';
import PanelLayout from '@/components/PanelLayout';
import StudioShell from '@/components/studio/StudioShell';
import WizardSidebar from '@/pages/KennelWizardV2/WizardSidebar';
import DogLivePreview from '@/components/wizard/DogLivePreview';
import PublishSuccessFlow from '@/components/wizard/PublishSuccessFlow';
import WizardSummaryStep from '@/components/wizard/WizardSummaryStep';
import { WizardTopbar } from '@/components/wizard/WizardTopbar';
import { WizardMobileBar } from '@/components/wizard/WizardMobileBar';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import ImageUploader from '@/components/ImageUploader';
import { ImageSeoBadge } from '@/components/ImageSeoBadge';
import FontStylePicker from '@/components/wizard/FontStylePicker';
import LivePagePreview from '@/components/wizard/LivePagePreview';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Dog, Cat, Heart, Camera, Crown, GitBranch,
  Loader2, Check, AlertCircle, ArrowLeft, ArrowRight, Plus, X, ChevronsUpDown, FileText, Wand2,
  Inbox, FolderOpen
} from 'lucide-react';
import { API } from '@/utils/api';
import { firePawConfetti } from '@/utils/confetti';

// ==================== CORE CREATOR IMPORTS ====================
import { 
  dogProfileSchema, 
  dogSteps,
  SEX_OPTIONS,
  ROLE_OPTIONS,
  HEALTH_TEST_OPTIONS,
  TITLE_OPTIONS,
  SHOW_RESULT_OPTIONS,
  calculateDogQualityScore,
  getDogMissingItems,
  hasSufficientGallery,
  hasHealthTests,
  hasTitles,
  hasRegistryNumber,
  hasParentageInfo,
  hasTrustElement,
  IMAGE_REQUIREMENTS
} from '@/creators/schemas/dog.profile.schema';
import { useTranslation } from 'react-i18next';

import { DOG_BREEDS, CAT_BREEDS } from '@/data/breeds';

// Health tests by breed category
const healthTestsByBreed = {
  large_dogs: [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'spondylosis', label: 'Spondyloza' },
    { id: 'dm', label: 'DM (mielopatia)' },
    { id: 'mdr1', label: 'MDR1' },
  ],
  medium_dogs: [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'pra', label: 'PRA (badanie oczu)' },
    { id: 'patella', label: 'Luxacja rzepki' },
  ],
  small_dogs: [
    { id: 'patella', label: 'Luxacja rzepki' },
    { id: 'pra', label: 'PRA (badanie oczu)' },
    { id: 'heart', label: 'Badanie serca' },
    { id: 'lcp', label: 'Legg-Calvé-Perthes' },
  ],
  cats: [
    { id: 'felv_fiv', label: 'FeLV/FIV' },
    { id: 'pkd', label: 'PKD' },
    { id: 'hcm', label: 'HCM' },
  ],
  default: [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'eyes', label: 'Badanie oczu' },
    { id: 'genetic', label: 'Badanie genetyczne' },
  ]
};

const breedToHealthCategory = {
  'Owczarek Niemiecki Krótkowłosy': 'large_dogs',
  'Owczarek Niemiecki Długowłosy': 'large_dogs',
  'Owczarek Belgijski Malinois': 'large_dogs',
  'Owczarek Belgijski Tervueren': 'large_dogs',
  'Owczarek Belgijski Groenendael': 'large_dogs',
  'Owczarek Podhalański': 'large_dogs',
  'Biały Owczarek Szwajcarski': 'large_dogs',
  'Owczarek Kaukaski': 'large_dogs',
  'Owczarek Środkowoazjatycki': 'large_dogs',
  'Labrador Retriever': 'large_dogs',
  'Golden Retriever': 'large_dogs',
  'Flat Coated Retriever': 'large_dogs',
  'Rottweiler': 'large_dogs',
  'Doberman': 'large_dogs',
  'Dog Niemiecki': 'large_dogs',
  'Cane Corso': 'large_dogs',
  'Leonberger': 'large_dogs',
  'Nowofundland': 'large_dogs',
  'Bernardyn': 'large_dogs',
  'Mastif Angielski': 'large_dogs',
  'Bullmastiff': 'large_dogs',
  'Berneński Pies Pasterski': 'large_dogs',
  'Duży Szwajcarski Pies Pasterski': 'large_dogs',
  'Rhodesian Ridgeback': 'large_dogs',
  'Irish Wolfhound': 'large_dogs',
  'Weimaraner': 'large_dogs',
  'American Staffordshire Terrier': 'medium_dogs',
  'Staffordshire Bull Terrier': 'medium_dogs',
  'Bokser': 'medium_dogs',
  'Border Collie': 'medium_dogs',
  'Australian Shepherd': 'medium_dogs',
  'Dalmatyńczyk': 'medium_dogs',
  'Husky Syberyjski': 'medium_dogs',
  'Samoyed': 'medium_dogs',
  'Beagle': 'medium_dogs',
  'Cocker Spaniel Angielski': 'medium_dogs',
  'Springer Spaniel': 'medium_dogs',
  'Buldog Angielski': 'medium_dogs',
  'Buldog Francuski': 'small_dogs',
  'Yorkshire Terrier': 'small_dogs',
  'Maltańczyk': 'small_dogs',
  'Chihuahua': 'small_dogs',
  'Shih Tzu': 'small_dogs',
  'Mops': 'small_dogs',
  'Pomeranian (Szpic Miniaturowy)': 'small_dogs',
  'Jack Russell Terrier': 'small_dogs',
  'Cavalier King Charles Spaniel': 'small_dogs',
  'Bichon Frise': 'small_dogs',
  'Papillon': 'small_dogs',
  'Boston Terrier': 'small_dogs',
};

// Step names for display
const stepNamesMap = dogSteps.reduce((acc, s) => {
  acc[s.id] = s.name;
  return acc;
}, {});

// ==================== BREED COMBOBOX ====================

function BreedCombobox({ breeds, value, onChange, hasError }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  const filtered = search
    ? breeds.filter(b => b.toLowerCase().includes(search.toLowerCase()))
    : breeds;

  const handleSelect = (breed) => {
    onChange(breed);
    setOpen(false);
    setSearch('');
  };

  const handleCustomSubmit = () => {
    if (search.trim().length >= 2) {
      onChange(search.trim());
      setOpen(false);
      setSearch('');
    }
  };

  // Zatwierdź wpisany tekst, gdy użytkownik nie kliknął pozycji z listy
  // (Enter lub zamknięcie listy) — żeby nie utknąć z pustym polem rasy.
  const commitSearch = () => {
    const s = search.trim();
    if (!s) return;
    const exact = breeds.find(b => b.toLowerCase() === s.toLowerCase());
    if (exact) onChange(exact);
    else if (filtered.length > 0) onChange(filtered[0]);
    else if (s.length >= 2) onChange(s);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) commitSearch(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={`flex h-12 w-full items-center justify-between rounded-md border px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
            hasError ? 'border-red-500' : 'border-input'
          } ${value ? 'text-foreground' : 'text-muted-foreground'}`}
          data-testid="dog-breed"
        >
          {value || 'Wyszukaj lub wpisz rase...'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder="Wpisz nazwe rasy..."
            value={search}
            onValueChange={setSearch}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (filtered.length > 0) handleSelect(filtered[0]);
                else handleCustomSubmit();
              }
            }}
            data-testid="breed-search-input"
          />
          <CommandList className="max-h-60">
            <CommandEmpty className="py-3 px-4">
              {search.trim().length >= 2 ? (
                <button
                  type="button"
                  onClick={handleCustomSubmit}
                  className="w-full text-left text-sm py-2 px-3 rounded-lg bg-orange-50 hover:bg-orange-100 transition-colors"
                  data-testid="breed-custom-add"
                >
                  <span className="font-semibold text-[#F47A3B]">+ Dodaj:</span>{' '}
                  <span className="font-medium">{search.trim()}</span>
                </button>
              ) : (
                <p className="text-sm text-muted-foreground">Wpisz min. 2 znaki...</p>
              )}
            </CommandEmpty>
            <CommandGroup>
              {filtered.map(breed => (
                <CommandItem
                  key={breed}
                  value={breed}
                  onSelect={() => handleSelect(breed)}
                  className="cursor-pointer"
                  data-testid={`breed-option-${breed.replace(/\s/g, '-').toLowerCase()}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === breed ? 'opacity-100' : 'opacity-0'}`} />
                  {breed}
                </CommandItem>
              ))}
            </CommandGroup>
            {search.trim().length >= 2 && filtered.length > 0 && !filtered.includes(search.trim()) && (
              <CommandGroup heading="Nie znalazles rasy?">
                <CommandItem onSelect={handleCustomSubmit} className="cursor-pointer" data-testid="breed-custom-add-bottom">
                  <Plus className="mr-2 h-4 w-4 text-[#F47A3B]" />
                  <span>Dodaj: <strong>{search.trim()}</strong></span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ==================== COMPONENT ====================

export default function DogWizard() {
  const { t } = useTranslation();
  const { kennelId: kennelIdFromPath, dogId: editDogIdFromPath, id: editDogIdFromId } = useParams();
  const [searchParams] = useSearchParams();
  const kennelIdFromQuery = searchParams.get('kennelId');
  // Dedykowany kreator: ?typ=suka | reproduktor → płeć z góry, tytuł kontekstowy.
  const dogType = searchParams.get('typ');
  const kennelIdFromUrl = kennelIdFromPath || kennelIdFromQuery;
  const editDogId = editDogIdFromPath || editDogIdFromId; // Support both /wizard/pies/:kennelId/:dogId and /panel/pies/:id/edytuj
  const isEdit = !!editDogId;
  const dogTitle = isEdit
    ? 'Edytuj profil'
    : dogType === 'suka' ? 'Dodaj sukę'
    : dogType === 'reproduktor' ? 'Dodaj reproduktora'
    : 'Dodaj psa';
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Klucz roboczego szkicu (per typ: suka / reproduktor / pies) — działa jak w Kreatorze Hodowli.
  const DOG_DRAFT_KEY = `apisai_dog_wizard_draft_${dogType || 'pies'}`;
  const draftRestoredRef = useRef(false);
  const firstDogAutosaveRef = useRef(true);
  
  const [step, setStep] = useState(1);
  // Kroki ukończone (zielony ✓) — akumulujemy po udanym „Dalej", spójnie z Kreatorem Hodowli.
  const [dogCompletedSteps, setDogCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addedDog, setAddedDog] = useState(null); // { name, id }
  const [successPhase, setSuccessPhase] = useState(null); // null | 'ask' | 'done'
  const [generating, setGenerating] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Kennel context
  const [kennel, setKennel] = useState(null);
  const [effectiveKennelId, setEffectiveKennelId] = useState(kennelIdFromUrl);
  
  // Quick Add Parent Modal state (for Step 2: Pochodzenie)
  const [availableParents, setAvailableParents] = useState([]);
  const [quickAddModal, setQuickAddModal] = useState({ open: false, type: null }); // type: 'sire' | 'dam'
  const [quickAddForm, setQuickAddForm] = useState({ name: '', breed: '', gender: '' });
  const [quickAddLoading, setQuickAddLoading] = useState(false);

  // ==================== FORM STATE ====================
  const buildInitialForm = () => ({
    // Step 1: Podstawy
    kennel_id: '',
    name: '',
    gender: dogType === 'reproduktor' ? 'male' : 'female',
    species: 'dog',
    breed: '',
    birth_date: '',
    color: '',
    role: 'breeding',
    listing_type: dogType === 'reproduktor' ? 'stud' : 'kennel_dog',
    // Warunki krycia (tylko reproduktor)
    stud_price: '',
    stud_conditions: '',
    show_in_catalog: true,
    font_style: 'elegant',
    
    // Step 2: Pochodzenie
    registry_number: '',
    pedigree_name: '',
    sire_id: '',     // ID ojca z bazy
    sire_name: '',
    sire_titles: '',
    dam_id: '',      // ID matki z bazy
    dam_name: '',
    dam_titles: '',
    
    // Step 3: Zdrowie
    selected_health_tests: [],
    health_test_results: {},
    health_notes: '',
    
    // Step 4: Osiągnięcia
    selected_titles: [],
    selected_show_results: [],
    achievements_text: '',
    
    // Step 5: Zdjęcia
    main_photo: '',
    gallery: [],
    description: '',
  });
  const [form, setForm] = useState(buildInitialForm);

  // ==================== DATA ADAPTERS ====================
  const normalizeDogDraft = (rawData) => {
    return {
      kennel_id: rawData.kennel_id || '',
      name: rawData.name || '',
      gender: rawData.gender || 'female',
      species: rawData.species || 'dog',
      breed: rawData.breed || '',
      birth_date: rawData.birth_date || '',
      color: rawData.color || '',
      role: rawData.role || 'breeding',
      listing_type: rawData.listing_type || (rawData.gender === 'male' ? 'stud' : 'kennel_dog'),
      stud_price: rawData.stud_price || '',
      stud_conditions: rawData.stud_conditions || '',
      show_in_catalog: rawData.show_in_catalog !== false,
      font_style: rawData.font_style || 'elegant',
      
      registry_number: rawData.registry_number || rawData.pedigree_number || '',
      pedigree_name: rawData.pedigree_name || '',
      sire_name: rawData.sire_name || '',
      sire_titles: rawData.sire_titles || '',
      dam_name: rawData.dam_name || '',
      dam_titles: rawData.dam_titles || '',
      
      selected_health_tests: rawData.selected_health_tests || [],
      health_test_results: rawData.health_test_results || rawData.health_tests || {},
      health_notes: rawData.health_notes || rawData.health_tests_text || '',
      
      selected_titles: rawData.selected_titles || [],
      selected_show_results: rawData.selected_show_results || rawData.selected_certificates || [],
      achievements_text: rawData.achievements_text || rawData.titles_text || '',
      
      main_photo: rawData.main_photo || '',
      gallery: rawData.gallery || [],
      description: rawData.description || '',
    };
  };

  const denormalizeDogDraft = (draft) => {
    // Reproduktor = samiec hodowli (ang. stud). Wcześniej na sztywno zapisywano
    // 'kennel_dog' — przez co reproduktory nie miały typu 'stud'. Naprawione.
    const draftIsStud = dogType === 'reproduktor' || (draft.gender === 'male' && dogType !== 'suka');
    return {
      kennel_id: effectiveKennelId || draft.kennel_id,
      name: draft.name,
      gender: draft.gender,
      species: draft.species,
      breed: draft.breed,
      birth_date: draft.birth_date,
      color: draft.color,
      role: draft.role,
      listing_type: draftIsStud ? 'stud' : 'kennel_dog',
      // Warunki krycia (reproduktor)
      stud_available: draftIsStud,
      stud_price: draft.stud_price || '',
      stud_conditions: draft.stud_conditions || '',
      show_in_catalog: draft.show_in_catalog !== false,
      font_style: draft.font_style || 'elegant',
      
      registry_number: draft.registry_number,
      pedigree_number: draft.registry_number, // Legacy
      pedigree_name: draft.pedigree_name,
      sire_name: draft.sire_name,
      sire_titles: draft.sire_titles,
      dam_name: draft.dam_name,
      dam_titles: draft.dam_titles,
      
      selected_health_tests: draft.selected_health_tests,
      health_tests: draft.health_test_results, // Legacy
      health_test_results: draft.health_test_results,
      health_tests_text: draft.health_notes,
      health_notes: draft.health_notes,
      
      selected_titles: draft.selected_titles,
      selected_certificates: draft.selected_show_results, // Legacy
      selected_show_results: draft.selected_show_results,
      titles_text: draft.achievements_text,
      achievements_text: draft.achievements_text,
      
      main_photo: draft.main_photo,
      gallery: draft.gallery || [],
      description: draft.description,
    };
  };

  // ==================== EFFECTS ====================
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user) {
      if (kennelIdFromUrl) {
        setEffectiveKennelId(kennelIdFromUrl);
        fetchKennel(kennelIdFromUrl);
      }
      if (isEdit && editDogId) {
        fetchDogData();
      }
    }
  }, [user, authLoading, kennelIdFromUrl, isEdit, editDogId, navigate]);

  // Wczytaj roboczy szkic (tylko nowy profil) — analogicznie do Kreatora Hodowli.
  useEffect(() => {
    if (isEdit) return;
    try {
      const raw = localStorage.getItem(DOG_DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft?.form) {
          setForm((prev) => ({ ...prev, ...draft.form }));
          if (draft.step) setStep(draft.step);
          if (Array.isArray(draft.dogCompletedSteps)) setDogCompletedSteps(draft.dogCompletedSteps);
          if (draft.form.breed) draftRestoredRef.current = true;
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autozapis roboczego szkicu (tylko nowy profil). Pomijamy pierwszy przebieg (mount),
  // by nie nadpisać wczytanego szkicu pustym formularzem (race z efektem wczytania).
  useEffect(() => {
    if (isEdit) return;
    if (firstDogAutosaveRef.current) { firstDogAutosaveRef.current = false; return; }
    try {
      localStorage.setItem(DOG_DRAFT_KEY, JSON.stringify({ form, step, dogCompletedSteps }));
    } catch {}
  }, [form, step, dogCompletedSteps, isEdit, DOG_DRAFT_KEY]);

  const fetchKennel = async (kId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/kennels/${kId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKennel(res.data);
      // Auto-set species and breed from kennel
      if (res.data.species?.length > 0 && !form.species) {
        updateForm('species', res.data.species[0]);
      }
      // Auto-uzupełnij rasę z hodowli TYLKO gdy to konkretna rasa.
      // Pomijamy placeholdery typu „Mieszany"/„Kundel" — użytkownik wybiera rasę sam,
      // dzięki czemu badania zdrowotne dopasują się do konkretnej rasy.
      const kennelBreed = res.data.breeds?.[0];
      const isMixedBreed = kennelBreed && /mieszan|mixed|kundel|wieloras/i.test(kennelBreed);
      if (kennelBreed && !isMixedBreed && !form.breed && !draftRestoredRef.current) {
        updateForm('breed', kennelBreed);
      }
      // Fetch available parents for pedigree
      fetchAvailableParents(kId);
    } catch (e) {
      console.error('Error fetching kennel:', e);
    }
  };

  const fetchDogData = async () => {
    setFetchingData(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dogs/${editDogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForm(normalizeDogDraft(res.data));
      if (res.data.kennel_id) {
        setEffectiveKennelId(res.data.kennel_id);
        fetchKennel(res.data.kennel_id);
        fetchAvailableParents(res.data.kennel_id);
      }
    } catch (e) {
      console.error('Error fetching dog:', e);
      toast.error('Nie udało się pobrać danych psa');
      navigate('/panel');
    } finally {
      setFetchingData(false);
    }
  };

  // Fetch available parents for pedigree selection
  const fetchAvailableParents = async (kId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/litter-wizard/parents?kennel_id=${kId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableParents(res.data.parents || []);
    } catch (e) {
      console.error('Error fetching parents:', e);
    }
  };

  // Quick Add Modal functions
  const openQuickAddModal = (type) => {
    const gender = type === 'dam' ? 'female' : 'male';
    setQuickAddForm({
      name: '',
      breed: form.breed || '',
      gender: gender
    });
    setQuickAddModal({ open: true, type });
  };

  const closeQuickAddModal = () => {
    setQuickAddModal({ open: false, type: null });
    setQuickAddForm({ name: '', breed: '', gender: '' });
  };

  const handleQuickAddSubmit = async () => {
    if (!quickAddForm.name || quickAddForm.name.trim().length < 2) {
      toast.error('Podaj imię (min. 2 znaki)');
      return;
    }
    if (!quickAddForm.breed) {
      toast.error('Wybierz rasę');
      return;
    }
    
    setQuickAddLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: quickAddForm.name.trim(),
        breed: quickAddForm.breed,
        gender: quickAddForm.gender,
        species: form.species,
        listing_type: 'kennel_dog'
      };
      
      const res = await axios.post(`${API}/kennels/${effectiveKennelId}/dogs/quick-add`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newDog = res.data;
      toast.success(`${quickAddForm.gender === 'female' ? 'Suka' : 'Pies'} "${newDog.name}" dodany!`);
      
      // Refresh parents list
      await fetchAvailableParents(effectiveKennelId);
      
      // Auto-fill parent fields
      if (quickAddModal.type === 'dam') {
        updateForm('dam_id', newDog.id);
        updateForm('dam_name', newDog.name);
      } else {
        updateForm('sire_id', newDog.id);
        updateForm('sire_name', newDog.name);
      }
      
      closeQuickAddModal();
    } catch (e) {
      console.error('Quick add error:', e);
      const detail = e.response?.data?.detail;
      let errorMsg = 'Błąd dodawania';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map(err => err.msg || err.message || 'Błąd walidacji').join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setQuickAddLoading(false);
    }
  };

  // Handle parent selection from dropdown
  const handleParentSelect = (type, parentId) => {
    if (parentId === 'manual') {
      if (type === 'sire') {
        updateForm('sire_id', '');
      } else {
        updateForm('dam_id', '');
      }
      return;
    }
    if (parentId === 'quick_add') {
      openQuickAddModal(type);
      return;
    }
    
    const parent = availableParents.find(p => p.id === parentId);
    if (type === 'sire') {
      updateForm('sire_id', parentId);
      updateForm('sire_name', parent?.name || '');
    } else {
      updateForm('dam_id', parentId);
      updateForm('dam_name', parent?.name || '');
    }
  };

  // ==================== FORM HELPERS ====================
  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const toggleSelection = (field, value) => {
    setForm(prev => {
      const current = prev[field] || [];
      const exists = current.includes(value);
      return {
        ...prev,
        [field]: exists ? current.filter(v => v !== value) : [...current, value]
      };
    });
  };

  // ==================== DERIVED STATE ====================
  const breeds = form.species === 'dog' ? DOG_BREEDS : CAT_BREEDS;

  // Tytuł lewej kolumny (KROKI) — dynamiczny, kontekstowy do typu zwierzęcia.
  const sidebarSubject = dogType === 'reproduktor' || form.gender === 'male'
    ? 'reproduktora'
    : (dogType === 'suka' || form.gender === 'female' ? 'suki' : 'psa');
  const sidebarTitle = (
    <>
      Budujemy prezentację{' '}
      <span className="text-orange-500">{sidebarSubject}</span>
    </>
  );
  // Kontekstowa nazwa kroku "Opis" (analogicznie do "O hodowli" w Kreatorze Hodowli)
  const descSubject = dogType === 'reproduktor' || form.gender === 'male'
    ? 'reproduktorze'
    : (dogType === 'suka' || form.gender === 'female' ? 'suce' : 'psie');
  const descStepName = `O ${descSubject}`;
  // Zlokalizowane kroki — krok opisu (id 5) dostaje kontekstową nazwę "O suce / O reproduktorze"
  // Reproduktor (samiec) dostaje dodatkowy krok "Warunki krycia" (id 7) — spójnie z resztą kreatorów.
  const isStud = dogType === 'reproduktor' || (form.gender === 'male' && dogType !== 'suka');
  // Krok końcowy „Podsumowanie" — taki sam jak w Kreatorze Hodowli (spójność 1:1).
  // ID dynamiczne: suka kończy na kroku 6 → podsumowanie = 7;
  // reproduktor ma dodatkowy krok 7 (Warunki krycia) → podsumowanie = 8.
  const summaryStepId = isStud ? 8 : 7;
  const localizedDogSteps = [
    ...dogSteps.map((s) =>
      s.id === 5 ? { ...s, name: descStepName, subtitle: 'Opowiedz własnymi słowami' } : s
    ),
    ...(isStud
      ? [{ id: 7, name: 'Warunki krycia', subtitle: 'Cena i wymagania wobec suki', icon: 'heart', fields: ['stud_price', 'stud_conditions'], mustFields: [], mustGate: false }]
      : []),
    { id: summaryStepId, name: 'Podsumowanie', subtitle: 'Opublikuj i zobacz efekty', icon: 'rocket', fields: [], mustFields: [], mustGate: false },
  ];
  const totalSteps = localizedDogSteps.length;
  const getHealthTestsForBreed = () => {
    if (form.species === 'cat') return healthTestsByBreed.cats;
    const category = breedToHealthCategory[form.breed] || 'default';
    return healthTestsByBreed[category] || healthTestsByBreed.default;
  };

  // ==================== QUALITY SCORE ====================
  const qualityScoreResult = calculateDogQualityScore(form);
  const { score: qualityScore, suggestions, status: scoreStatus } = qualityScoreResult;
  const missingItems = getDogMissingItems(form);

  // ==================== VALIDATION ====================
  const validateStep = () => {
    const stepErrors = {};
    
    // Step 1: Basic info (MUST)
    if (step === 1) {
      if (!form.name || form.name.trim().length < 2) {
        stepErrors.name = 'Podaj imię psa';
      }
      if (!form.gender) {
        stepErrors.gender = 'Wybierz płeć';
      }
      if (!form.breed) {
        stepErrors.breed = 'Wybierz rasę';
      }
      if (!form.birth_date) {
        stepErrors.birth_date = 'Podaj datę urodzenia';
      }
      setErrors(stepErrors);
      return Object.keys(stepErrors).length === 0;
    }
    
    // Steps 2, 3, 4, 5 (Opis) are optional
    if (step >= 2 && step <= 5) {
      return true;
    }
    
    // Step 6: Photos (MUST)
    if (step === 6) {
      if (!form.main_photo) {
        stepErrors.main_photo = 'Dodaj zdjęcie główne';
      }
      if (!hasSufficientGallery(form)) {
        stepErrors.gallery = 'Dodaj min. 1 zdjęcie do galerii';
      }
      setErrors(stepErrors);
      return Object.keys(stepErrors).length === 0;
    }
    
    return true;
  };

  const isStepValid = () => {
    // Step 1: Basic info
    if (step === 1) {
      return form.name && form.name.trim().length >= 2 && form.gender && form.breed && form.birth_date;
    }
    
    // Steps 2, 3, 4, 5 (Opis) are optional - always valid
    if (step >= 2 && step <= 5) {
      return true;
    }
    
    // Step 6: Photos
    if (step === 6) {
      return form.main_photo && hasSufficientGallery(form);
    }
    
    return true;
  };

  // ==================== NAVIGATION ====================
  const handleNext = () => {
    const isValid = validateStep();
    if (isValid) {
      setDogCompletedSteps((prev) => Array.from(new Set([...prev, step])));
      setStep(step + 1);
      setErrors({});
      return;
    }
    // Pokaż konkretny komunikat, czego brakuje (zamiast cicho blokować przycisk).
    if (step === 1) {
      if (!form.name || form.name.trim().length < 2) {
        toast.error('Podaj imię (min. 2 znaki)');
      } else if (!form.breed) {
        toast.error('Wybierz rasę — wpisz i naciśnij Enter lub wybierz z listy');
      } else if (!form.birth_date) {
        toast.error('Podaj datę urodzenia');
      } else if (!form.gender) {
        toast.error('Wybierz płeć');
      }
    } else if (step === 6) {
      if (!form.main_photo) toast.error('Dodaj zdjęcie główne');
      else if (!hasSufficientGallery(form)) toast.error('Dodaj min. 1 zdjęcie do galerii');
    }
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const fieldEl = document.querySelector(`[data-testid="dog-${firstErrorField}"], [name="${firstErrorField}"]`);
      fieldEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setErrors({});
    }
  };

  const goToStep = (targetStep) => {
    setStep(targetStep);
    setErrors({});
  };

  // ==================== EKRAN SUKCESU: DODAJ KOLEJNĄ / DALEJ ====================
  const subjectAccusative = dogType === 'reproduktor' ? 'kolejnego reproduktora' : dogType === 'suka' ? 'kolejną suczkę' : 'kolejnego psa';
  const listRoute = dogType === 'reproduktor' ? '/panel/reproduktory' : dogType === 'suka' ? '/panel/psy' : '/panel/profil';

  // ==================== ASYSTENT OPISU (bez nazwy „AI") ====================
  const generateDescription = async () => {
    if (!form.breed) {
      toast.error('Najpierw wybierz rasę w kroku „Podstawy"');
      return;
    }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      // Mapuj ID na czytelne etykiety, by w opisie pojawiły się pełne nazwy.
      const titleLabels = (form.selected_titles || [])
        .map((id) => TITLE_OPTIONS.find((t) => t.id === id)?.label || id);
      const showLabels = (form.selected_show_results || [])
        .map((id) => SHOW_RESULT_OPTIONS.find((s) => s.id === id)?.label || id);
      const healthLabels = (form.selected_health_tests || [])
        .map((id) => getHealthTestsForBreed().find((t) => t.id === id)?.label || id);
      const res = await axios.post(`${API}/ai/generate`, {
        type: 'dog_description',
        style: 'premium',
        kennel_id: effectiveKennelId || undefined,
        context: {
          dog_name: form.name,
          breed: form.breed,
          gender: form.gender,
          color: form.color,
          titles: [...titleLabels, ...showLabels].join(', '),
          health_tests: healthLabels.join(', '),
          parents: [form.dam_name, form.sire_name].filter(Boolean).join(' × '),
        },
      }, { headers: { Authorization: `Bearer ${token}` } });
      updateForm('description', res.data.content);
      toast.success('Gotowe — propozycja opisu wstawiona');
    } catch (e) {
      toast.error('Nie udało się napisać opisu. Spróbuj ponownie.');
    } finally {
      setGenerating(false);
    }
  };

  // Asystent „Warunki krycia" (reproduktor) — bez nazwy „AI".
  const generateStudConditions = async () => {
    if (!form.breed) {
      toast.error('Najpierw wybierz rasę w kroku „Podstawy"');
      return;
    }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const healthLabels = (form.selected_health_tests || [])
        .map((id) => getHealthTestsForBreed().find((t) => t.id === id)?.label || id);
      const res = await axios.post(`${API}/ai/generate`, {
        type: 'stud_conditions',
        style: 'premium',
        kennel_id: effectiveKennelId || undefined,
        context: {
          dog_name: form.name,
          breed: form.breed,
          gender: form.gender,
          health_tests: healthLabels.join(', '),
        },
      }, { headers: { Authorization: `Bearer ${token}` } });
      updateForm('stud_conditions', res.data.content);
      toast.success('Gotowe — propozycja warunków krycia wstawiona');
    } catch (e) {
      toast.error('Nie udało się napisać. Spróbuj ponownie.');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddAnother = () => {
    setAddedDog(null);
    setSuccessPhase(null);
    setForm(buildInitialForm());
    setTermsAccepted(false);
    setErrors({});
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFinishAdding = () => {
    setAddedDog(null);
    setSuccessPhase(null);
    navigate(listRoute);
  };

  // „Zapisz i wróć" — zapisujemy bieżący szkic lokalnie i wracamy do panelu (jak w Kreatorze Hodowli).
  const handleSaveAndExit = () => {
    if (!isEdit) {
      try { localStorage.setItem(DOG_DRAFT_KEY, JSON.stringify({ form, step, dogCompletedSteps })); } catch {}
    }
    toast.success('Zapisano. Możesz wrócić w każdej chwili.');
    navigate('/panel');
  };

  // ==================== SUBMIT ====================
  const handleSubmit = async () => {
    // Check terms acceptance
    if (!isEdit && !termsAccepted) {
      toast.error('Zaakceptuj warunki publikacji');
      return;
    }
    
    // Validate MUST fields
    if (!form.name || form.name.trim().length < 2) {
      toast.error('Podaj imię psa');
      setStep(1);
      return;
    }
    
    if (!form.gender || !form.breed) {
      toast.error('Uzupełnij podstawowe dane');
      setStep(1);
      return;
    }
    
    if (!form.main_photo) {
      toast.error('Dodaj zdjęcie główne');
      setStep(6);
      return;
    }
    
    if (!hasSufficientGallery(form)) {
      toast.error('Dodaj min. 1 zdjęcie do galerii');
      setStep(6);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = denormalizeDogDraft(form);

      let res;
      if (isEdit) {
        res = await axios.put(`${API}/dogs/${editDogId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pies zaktualizowany!');
        navigate(effectiveKennelId ? '/panel/profil' : '/panel');
      } else {
        res = await axios.post(`${API}/kennels/${effectiveKennelId}/dogs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Pies dodany!');
        firePawConfetti();
        try { localStorage.removeItem(DOG_DRAFT_KEY); } catch {}
        // Ujednolicony ekran sukcesu (taki sam jak w Kreatorze Hodowli).
        setAddedDog({ name: form.name, id: res.data?.id, slug: res.data?.slug });
        setSuccessPhase('done');
      }
    } catch (e) {
      console.error('Submit error:', e);
      if (e.response?.status === 401) {
        toast.error('Sesja wygasła. Zaloguj się ponownie.');
      } else {
        // Handle Pydantic validation errors
        const detail = e.response?.data?.detail;
        let errorMsg = 'Błąd zapisu. Spróbuj ponownie.';
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic returns array of validation errors
          errorMsg = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        }
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== LOADING STATE ====================
  if (fetchingData) {
    return (
      <StudioShell mode="workspace" contextLabel={dogTitle} activeKey="dogs" sidebarMuted={true} backTo="/panel" backLabel="Wróć do panelu">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
        </div>
      </StudioShell>
    );
  }

  // Pasek górny — wspólny WizardTopbar (jedno źródło prawdy dla wszystkich kreatorów)
  const dogProgressPct = Math.round((step / totalSteps) * 100);
  const dogStepName = (localizedDogSteps.find((s) => s.id === step) || {}).name || stepNamesMap[step];
  const dogTopbarSlot = (
    <WizardTopbar
      prefix="dog-wizard"
      step={step}
      totalSteps={totalSteps}
      stepName={dogStepName}
      progressPct={dogProgressPct}
      isLastStep={step === totalSteps}
      submitting={loading}
      nextDisabled={loading || (step === totalSteps && !termsAccepted && !isEdit)}
      publishLabel={isEdit ? 'Zapisz zmiany' : dogTitle}
      onSaveExit={handleSaveAndExit}
      onNext={step === totalSteps ? handleSubmit : handleNext}
    />
  );

  // ==================== PODSUMOWANIE (krok końcowy) ====================
  const dogIsFemale = form.gender === 'female';
  const dogPlec = isStud ? 'Reproduktor (samiec)' : (dogIsFemale ? 'Suka' : form.gender === 'male' ? 'Samiec' : null);
  const dogDescLen = (form.description || '').trim().length;
  const dogHasMainPhoto = !!form.main_photo;
  const dogHealthCount = (form.selected_health_tests || []).length;
  const dogSummaryRows = [
    { icon: 'dog', label: 'Imię', ok: !!form.name, value: form.name || 'Uzupełnij imię', hint: 'Imię psa wyświetlane na profilu i w katalogu.', step: 1, required: true },
    { icon: 'paw', label: 'Rasa', ok: !!form.breed, value: form.breed || 'Wybierz rasę', hint: 'Po rasie klienci filtrują katalog — wybierz właściwą.', step: 1, required: true },
    { icon: 'heart', label: 'Płeć', ok: !!dogPlec, value: dogPlec || 'Wybierz płeć', hint: 'Określ, czy to suka czy reproduktor.', step: 1, required: true },
    ...(isStud ? [{ icon: 'money', label: 'Cena krycia', ok: !!form.stud_price, value: form.stud_price ? `${form.stud_price} zł` : 'Brak ceny', hint: 'Podaj cenę krycia — to pierwsze, o co pytają właściciele suk.', step: 7, addLabel: 'Uzupełnij' }] : []),
    { icon: 'photo', label: 'Zdjęcie główne', ok: dogHasMainPhoto, value: dogHasMainPhoto ? 'Dodane' : 'Brak zdjęcia', hint: 'Profil ze zdjęciem dostaje znacznie więcej zapytań.', step: 6, addLabel: 'Dodaj' },
    { icon: 'gallery', label: 'Galeria', ok: (form.gallery || []).length > 0, value: (form.gallery || []).length > 0 ? `${(form.gallery || []).length} zdjęć` : 'Brak zdjęć', hint: 'Dodaj kilka zdjęć z różnych ujęć.', step: 6, addLabel: 'Dodaj' },
    { icon: 'dna', label: 'Badania zdrowotne', ok: dogHealthCount > 0, value: dogHealthCount > 0 ? `${dogHealthCount} badań` : 'Brak badań', hint: 'Wyniki badań budują zaufanie i wyróżniają Twojego psa.', step: 3, addLabel: 'Dodaj' },
    { icon: 'text', label: 'Opis', ok: dogDescLen >= 80, value: dogDescLen ? (dogDescLen < 80 ? `Krótki opis (${dogDescLen} znaków)` : 'Dodany') : 'Brak opisu', hint: 'Opowiedz o charakterze i pochodzeniu psa.', step: 5, addLabel: 'Uzupełnij' },
  ];

  // ==================== RENDER ====================
  return (
    <StudioShell mode="workspace" contextLabel={dogTitle} topbarSlot={dogTopbarSlot} activeKey="dogs" sidebarMuted={true} backTo="/panel" backLabel="Wróć do panelu">
      <div data-testid="dog-wizard-v2">
      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 pb-24 lg:pb-8">
        <div className="flex flex-col lg:flex-row gap-6 xl:gap-8 items-start">
          {/* LEWA KOLUMNA: KROKI — ukryta na kroku Podsumowanie (pełna szerokość) */}
          {step !== totalSteps && (
          <WizardSidebar
            steps={localizedDogSteps}
            currentStep={step}
            completedSteps={dogCompletedSteps}
            onStepClick={(id) => goToStep(id)}
            title={sidebarTitle}
          />
          )}

          {/* ŚRODEK: FORMULARZ (na kroku Podsumowanie — treść od lewej, bez ramki karty; podgląd zostaje po prawej) */}
          <section className="flex-1 min-w-0 w-full" data-testid="dog-wizard-form-panel">
            {/* Wizard Card */}
            <div className={step === totalSteps ? '' : 'apisai-soft-card overflow-hidden'}>
          <AnimatePresence mode="wait">
            {/* STEP 1: Podstawy */}
            {step === 1 && (
              <WizardStep
                key="step1"
                icon={Dog}
                title="Podstawowe informacje"
                subtitle={`Podstawowe dane ${sidebarSubject} — tworzą wizytówkę widoczną w Google i katalogu APISAI.`}
              >
                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <Label>Imię rodowe *</Label>
                    <Input
                      data-testid="dog-name"
                      value={form.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      placeholder="np. Luna z Doliny Słońca"
                      className={`h-12 ${errors.name ? 'border-red-500' : ''}`}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  {/* Gender — ukryte w dedykowanym kreatorze i w edycji (płeć ustalona z góry / z danych) */}
                  {!dogType && !isEdit && (
                  <div>
                    <Label>Płeć *</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => updateForm('gender', 'female')}
                        className={`p-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                          form.gender === 'female' 
                            ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' 
                            : 'border-zinc-200 dark:border-gray-700'
                        }`}
                      >
                        <span className="text-2xl">♀</span>
                        <span className="font-medium">Suka</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateForm('gender', 'male')}
                        className={`p-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                          form.gender === 'male' 
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                            : 'border-zinc-200 dark:border-gray-700'
                        }`}
                      >
                        <span className="text-2xl">♂</span>
                        <span className="font-medium">Pies</span>
                      </button>
                    </div>
                    {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
                  </div>
                  )}

                  {/* Breed — searchable combobox with custom input */}
                  <div>
                    <Label>Rasa *</Label>
                    <BreedCombobox
                      breeds={breeds}
                      value={form.breed}
                      onChange={(v) => updateForm('breed', v)}
                      hasError={!!errors.breed}
                    />
                    {errors.breed && <p className="text-red-500 text-sm mt-1">{errors.breed}</p>}
                  </div>

                  {/* Birth date & Color */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data urodzenia</Label>
                      <Input
                        type="date"
                        value={form.birth_date}
                        onChange={(e) => updateForm('birth_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Umaszczenie</Label>
                      <Input
                        value={form.color}
                        onChange={(e) => updateForm('color', e.target.value)}
                        placeholder="np. Czarno-podpalany"
                      />
                    </div>
                  </div>

                  {/* Role */}
                  <div>
                    <Label>Rola</Label>
                    <Select value={form.role} onValueChange={(v) => updateForm('role', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 2: Pochodzenie */}
            {step === 2 && (
              <WizardStep
                key="step2"
                icon={GitBranch}
                title="Pochodzenie"
                subtitle="Rodowód i rodzice budują zaufanie i pokazują wartość hodowlaną."
              >
                <div className="space-y-4">
                  {/* Registry number */}
                  <div>
                    <Label>Numer rodowodu / chip</Label>
                    <Input
                      value={form.registry_number}
                      onChange={(e) => updateForm('registry_number', e.target.value)}
                      placeholder="np. PKR.I-12345 lub numer chip"
                    />
                  </div>

                  {/* Pedigree name */}
                  <div>
                    <Label>Pełna nazwa rodowodowa</Label>
                    <Input
                      value={form.pedigree_name}
                      onChange={(e) => updateForm('pedigree_name', e.target.value)}
                      placeholder="np. Luna z Doliny Słońca FCI"
                    />
                  </div>

                  {/* Parents */}
                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-3">Rodzice</h4>
                    
                    {/* Sire (Ojciec) */}
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20">
                        <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                          <span className="text-blue-500">♂</span> Ojciec
                        </Label>
                        
                        {availableParents.filter(p => p.gender === 'male').length > 0 ? (
                          <div className="space-y-2">
                            <Select 
                              value={form.sire_id} 
                              onValueChange={(v) => handleParentSelect('sire', v)}
                            >
                              <SelectTrigger data-testid="dog-sire-select">
                                <SelectValue placeholder="Wybierz z bazy lub dodaj..." />
                              </SelectTrigger>
                              <SelectContent>
                                {availableParents.filter(p => p.gender === 'male').map(parent => (
                                  <SelectItem key={parent.id} value={parent.id}>
                                    <div className="flex items-center gap-2">
                                      {parent.main_photo && (
                                        <img src={parent.main_photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                                      )}
                                      <span>{parent.name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                                <SelectItem value="quick_add">
                                  <span className="text-[#F47A3B] font-medium flex items-center gap-1">
                                    <Plus className="w-4 h-4" /> Dodaj nowego psa
                                  </span>
                                </SelectItem>
                                <SelectItem value="manual">
                                  <span className="text-zinc-500">✎ Wpisz ręcznie</span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {!form.sire_id && (
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  value={form.sire_name}
                                  onChange={(e) => updateForm('sire_name', e.target.value)}
                                  placeholder="Imię ojca"
                                  className="h-9"
                                />
                                <Input
                                  value={form.sire_titles}
                                  onChange={(e) => updateForm('sire_titles', e.target.value)}
                                  placeholder="Tytuły (np. Ch.PL)"
                                  className="h-9"
                                />
                              </div>
                            )}
                            
                            {form.sire_id && (
                              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                                <Check className="w-3 h-3" />
                                Powiązany z profilem w bazie
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                value={form.sire_name}
                                onChange={(e) => updateForm('sire_name', e.target.value)}
                                placeholder="Imię ojca"
                                className="h-9"
                              />
                              <Input
                                value={form.sire_titles}
                                onChange={(e) => updateForm('sire_titles', e.target.value)}
                                placeholder="Tytuły (np. Ch.PL)"
                                className="h-9"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => openQuickAddModal('sire')}
                              className="text-xs text-[#F47A3B] hover:underline flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" /> Lub dodaj ojca do bazy
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Dam (Matka) */}
                      <div className="p-4 rounded-xl border border-pink-200 dark:border-pink-800 bg-pink-50/50 dark:bg-pink-900/20">
                        <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                          <span className="text-pink-500">♀</span> Matka
                        </Label>
                        
                        {availableParents.filter(p => p.gender === 'female').length > 0 ? (
                          <div className="space-y-2">
                            <Select 
                              value={form.dam_id} 
                              onValueChange={(v) => handleParentSelect('dam', v)}
                            >
                              <SelectTrigger data-testid="dog-dam-select">
                                <SelectValue placeholder="Wybierz z bazy lub dodaj..." />
                              </SelectTrigger>
                              <SelectContent>
                                {availableParents.filter(p => p.gender === 'female').map(parent => (
                                  <SelectItem key={parent.id} value={parent.id}>
                                    <div className="flex items-center gap-2">
                                      {parent.main_photo && (
                                        <img src={parent.main_photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                                      )}
                                      <span>{parent.name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                                <SelectItem value="quick_add">
                                  <span className="text-[#F47A3B] font-medium flex items-center gap-1">
                                    <Plus className="w-4 h-4" /> Dodaj nową sukę
                                  </span>
                                </SelectItem>
                                <SelectItem value="manual">
                                  <span className="text-zinc-500">✎ Wpisz ręcznie</span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {!form.dam_id && (
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  value={form.dam_name}
                                  onChange={(e) => updateForm('dam_name', e.target.value)}
                                  placeholder="Imię matki"
                                  className="h-9"
                                />
                                <Input
                                  value={form.dam_titles}
                                  onChange={(e) => updateForm('dam_titles', e.target.value)}
                                  placeholder="Tytuły (np. Ch.PL)"
                                  className="h-9"
                                />
                              </div>
                            )}
                            
                            {form.dam_id && (
                              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                                <Check className="w-3 h-3" />
                                Powiązana z profilem w bazie
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                value={form.dam_name}
                                onChange={(e) => updateForm('dam_name', e.target.value)}
                                placeholder="Imię matki"
                                className="h-9"
                              />
                              <Input
                                value={form.dam_titles}
                                onChange={(e) => updateForm('dam_titles', e.target.value)}
                                placeholder="Tytuły (np. Ch.PL)"
                                className="h-9"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => openQuickAddModal('dam')}
                              className="text-xs text-[#F47A3B] hover:underline flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" /> Lub dodaj matkę do bazy
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 3: Zdrowie */}
            {step === 3 && (
              <WizardStep
                key="step3"
                icon={Heart}
                title="Badania zdrowotne"
                subtitle="Dowód odpowiedzialnej hodowli — kupujący pytają o badania najczęściej."
              >
                <div className="space-y-4">
                  {!form.breed ? (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2 text-sm text-amber-800" data-testid="dog-health-no-breed">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Najpierw wybierz rasę w kroku „Podstawy" — badania zdrowotne dopasują się do konkretnej rasy.</span>
                    </div>
                  ) : (
                  <>
                  <div>
                    <Label>Badania zdrowotne <span className="text-zinc-400 font-normal">· {form.breed}</span></Label>
                    <p className="text-xs text-zinc-500 mb-3">Wybrane badania budują zaufanie</p>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {getHealthTestsForBreed().map(test => (
                        <label 
                          key={test.id} 
                          className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                            form.selected_health_tests.includes(test.id)
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                              : 'border-zinc-200 dark:border-gray-700 hover:border-zinc-300'
                          }`}
                        >
                          <Checkbox
                            checked={form.selected_health_tests.includes(test.id)}
                            onCheckedChange={() => toggleSelection('selected_health_tests', test.id)}
                          />
                          <span className="text-sm">{test.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Health test results */}
                  {form.selected_health_tests.length > 0 && (
                    <div className="pt-4 border-t">
                      <Label>Wyniki badań (opcjonalnie)</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {form.selected_health_tests.map(testId => {
                          const test = getHealthTestsForBreed().find(t => t.id === testId);
                          return (
                            <div key={testId}>
                              <Label className="text-xs">{test?.label || testId}</Label>
                              <Input
                                value={form.health_test_results[testId] || ''}
                                onChange={(e) => updateForm('health_test_results', {
                                  ...form.health_test_results,
                                  [testId]: e.target.value
                                })}
                                placeholder="np. A, 0/0, N/N"
                                className="h-9"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  </>
                  )}

                  {/* Additional notes */}
                  <div>
                    <Label>Dodatkowe informacje zdrowotne</Label>
                    <Textarea
                      value={form.health_notes}
                      onChange={(e) => updateForm('health_notes', e.target.value)}
                      placeholder="Inne badania, certyfikaty..."
                      rows={3}
                    />
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 4: Osiągnięcia */}
            {step === 4 && (
              <WizardStep
                key="step4"
                icon={Crown}
                title="Osiągnięcia"
                subtitle={`Tytuły i sukcesy wystawowe wyróżniają ${sidebarSubject} na tle innych.`}
              >
                <div className="space-y-4">
                  {/* Titles */}
                  <div>
                    <Label>Tytuły</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {TITLE_OPTIONS.map(opt => (
                        <label 
                          key={opt.id} 
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                            form.selected_titles.includes(opt.id)
                              ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                              : 'border-zinc-200 dark:border-gray-700'
                          }`}
                        >
                          <Checkbox
                            checked={form.selected_titles.includes(opt.id)}
                            onCheckedChange={() => toggleSelection('selected_titles', opt.id)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Show results */}
                  <div>
                    <Label>Wyniki wystawowe</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {SHOW_RESULT_OPTIONS.map(opt => (
                        <label 
                          key={opt.id} 
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                            form.selected_show_results.includes(opt.id)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-zinc-200 dark:border-gray-700'
                          }`}
                        >
                          <Checkbox
                            checked={form.selected_show_results.includes(opt.id)}
                            onCheckedChange={() => toggleSelection('selected_show_results', opt.id)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Other achievements */}
                  <div>
                    <Label>Inne osiągnięcia</Label>
                    <Textarea
                      value={form.achievements_text}
                      onChange={(e) => updateForm('achievements_text', e.target.value)}
                      placeholder="Opisz inne osiągnięcia..."
                      rows={3}
                    />
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 5: Opis */}
            {step === 5 && (
              <WizardStep
                key="step5"
                icon={FileText}
                title={descStepName}
                subtitle={`Opis własnymi słowami pomaga klientowi poczuć charakter ${sidebarSubject}.`}
              >
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="mb-0">Opis {dogType === 'reproduktor' ? 'reproduktora' : dogType === 'suka' ? 'suki' : 'psa'}</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={generateDescription}
                        disabled={generating}
                        data-testid="dog-ai-description-btn"
                        className="h-8 text-[#F47A3B] border-[#F47A3B]/30 hover:bg-[#F47A3B]/5"
                      >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                        {form.description ? 'Napisz od nowa' : 'Napisz za mnie'}
                      </Button>
                    </div>
                    <Textarea
                      data-testid="dog-description"
                      value={form.description}
                      onChange={(e) => updateForm('description', e.target.value)}
                      placeholder="Opisz charakter, temperament, usposobienie... lub kliknij Napisz za mnie."
                      rows={8}
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      ✏️ Możesz napisać sam lub kliknąć „Napisz za mnie" — opis zawsze pozostaje edytowalny.
                    </p>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 6: Zdjęcia */}
            {step === 6 && (
              <WizardStep
                key="step6"
                icon={Camera}
                title="Zdjęcia"
                subtitle="Zdjęcia decydują o pierwszym wrażeniu — dobre foto to więcej zapytań."
              >
                <div className="space-y-4">
                  {/* Zaufanie: zdjęcia zoptymalizowane pod SEO/AI */}
                  <ImageSeoBadge />
                  {/* Info banner — auto-cover from gallery */}
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300 text-sm">
                    💡 <strong>Wskazówka:</strong> Pierwsze zdjęcie z galerii zostanie automatycznie ustawione jako <strong>główne</strong>. Możesz to zmienić klikając „Ustaw jako główne" na dowolnej miniaturce.
                  </div>

                  {/* Combined image uploader for cover + gallery */}
                  <ImageUploader
                    coverImage={form.main_photo}
                    gallery={form.gallery || []}
                    onCoverChange={(url) => updateForm('main_photo', url)}
                    onGalleryChange={(urls) => updateForm('gallery', urls)}
                    maxGalleryImages={10}
                  />
                  {errors.main_photo && <p className="text-red-500 text-sm mt-1">{errors.main_photo}</p>}
                  {errors.gallery && <p className="text-red-500 text-sm mt-1">{errors.gallery}</p>}
                </div>
              </WizardStep>
            )}

            {/* STEP 7: Warunki krycia — tylko reproduktor (samiec) */}
            {step === 7 && isStud && (
              <WizardStep
                key="step7"
                icon={Heart}
                title="Warunki krycia"
                subtitle="Cena krycia i wymagania wobec suki — to przyspiesza decyzję właściciela suki."
              >
                <div className="space-y-4">
                  <div>
                    <Label>Cena krycia</Label>
                    <Input
                      data-testid="dog-stud-price"
                      value={form.stud_price}
                      onChange={(e) => updateForm('stud_price', e.target.value)}
                      placeholder='np. 2000 zł lub "szczenię z miotu"'
                      className="h-12"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Możesz podać kwotę lub formę rozliczenia (np. wybór szczenięcia z miotu).
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="mb-0">Warunki krycia — wymagania wobec suki</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={generateStudConditions}
                        disabled={generating}
                        data-testid="dog-stud-conditions-help-btn"
                        className="h-8 text-[#F47A3B] border-[#F47A3B]/30 hover:bg-[#F47A3B]/5"
                      >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                        {form.stud_conditions ? 'Napisz od nowa' : 'Napisz za mnie'}
                      </Button>
                    </div>
                    <Textarea
                      data-testid="dog-stud-conditions"
                      value={form.stud_conditions}
                      onChange={(e) => updateForm('stud_conditions', e.target.value)}
                      placeholder="np. Suka po badaniach HD/ED, z rodowodem ZKwP/FCI, w odpowiednim wieku, zdrowa i zaszczepiona..."
                      rows={6}
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Opisz jakie warunki musi spełnić suka: badania zdrowotne, rodowód, wiek, szczepienia itp.
                    </p>
                  </div>

                </div>
              </WizardStep>
            )}

            {/* STEP KOŃCOWY: Podsumowanie — ujednolicony z Kreatorem Hodowli (1:1) */}
            {step === summaryStepId && (
              <div key="step-summary">
                <div className="mb-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <FontStylePicker value={form.font_style} onChange={(v) => setForm(prev => ({ ...prev, font_style: v }))} />
                    <LivePagePreview
                      fontStyle={form.font_style}
                      eyebrow={form.breed || (isStud ? 'Reproduktor' : 'Suka')}
                      title={form.name || 'Twój pies'}
                      subtitle={[form.city, form.voivodeship].filter(Boolean).join(', ')}
                      image={form.main_photo || (form.gallery && form.gallery[0]) || ''}
                      accentKey="gold"
                      theme="dark"
                    />
                  </div>
                </div>
                <WizardSummaryStep
                  headerTitle={isStud ? 'Profil reproduktora jest gotowy do publikacji' : 'Profil suki jest gotowy do publikacji'}
                  headerSubtitle="Po publikacji profil pojawi się w katalogu APISAI i na stronie Twojej hodowli."
                  pageCardTitle={isStud ? 'Profil reproduktora' : 'Profil suki'}
                  checklist={dogSummaryRows}
                  goToStep={goToStep}
                  catalogChecked={form.show_in_catalog}
                  onCatalogChange={(v) => updateForm('show_in_catalog', v)}
                  catalogLabel="Chcę, aby ten profil był widoczny w katalogu APISAI."
                  catalogTestId="dog-catalog-toggle"
                  termsAccepted={termsAccepted}
                  setTermsAccepted={setTermsAccepted}
                  termsTestId="dog-terms-checkbox"
                  dataAccurateTestId="dog-data-accurate"
                  onPublish={handleSubmit}
                  onSaveLater={handleSaveAndExit}
                  submitting={loading}
                  isEdit={isEdit}
                  publishLabel={isStud ? 'Opublikuj reproduktora' : 'Opublikuj sukę'}
                  testId="dog-wizard-summary"
                />
              </div>
            )}
          </AnimatePresence>
            </div>
          </section>

          {/* PRAWA KOLUMNA: PODGLĄD NA ŻYWO — widoczny też na kroku Podsumowanie (układ „Birch Haus") */}
          <section className="hidden lg:block w-full lg:w-[420px] xl:w-[460px] flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <DogLivePreview form={form} />
            </div>
          </section>
        </div>
      </main>

      {/* MOBILE: wspólny WizardMobileBar (pływający podgląd + dolny pasek akcji + arkusz) */}
      <WizardMobileBar
        prefix="dog-wizard"
        step={step}
        totalSteps={totalSteps}
        isLastStep={step === totalSteps}
        submitting={loading}
        nextDisabled={loading || (step === totalSteps && !termsAccepted && !isEdit)}
        publishLabel={isEdit ? 'Zapisz zmiany' : dogTitle}
        onBack={handleBack}
        onSaveExit={handleSaveAndExit}
        onNext={step === totalSteps ? handleSubmit : handleNext}
        previewNode={<DogLivePreview form={form} />}
      />
      </div>
      
      {/* Ujednolicony ekran sukcesu — identyczny we wszystkich kreatorach */}
      {successPhase === 'done' && (
        <PublishSuccessFlow
          entityType={isStud ? 'reproduktor' : 'suka'}
          slug={addedDog?.slug}
          userName={user?.name}
          entityName={addedDog?.name || form.name}
          coverImage={form.main_photo || (form.gallery && form.gallery[0])}
          subtitle={form.breed}
          onClose={handleFinishAdding}
          onNavigate={(path) => { setSuccessPhase(null); navigate(path); }}
          nextItems={[
            { key: 'add', icon: Plus, label: isStud ? 'Dodaj kolejnego reproduktora' : 'Dodaj kolejną sukę', action: handleAddAnother },
            { key: 'leads', icon: Inbox, label: 'Przejdź do zapytań', path: '/panel/zapytania' },
            { key: 'list', icon: FolderOpen, label: isStud ? 'Lista reproduktorów' : 'Lista suk', action: handleFinishAdding },
          ]}
        />
      )}

      {/* Quick Add Parent Modal */}
      <Dialog open={quickAddModal.open} onOpenChange={(open) => !open && closeQuickAddModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {quickAddModal.type === 'dam' ? (
                <>
                  <span className="text-pink-500">♀</span>
                  Dodaj matkę
                </>
              ) : (
                <>
                  <span className="text-blue-500">♂</span>
                  Dodaj ojca
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Szybko dodaj {quickAddModal.type === 'dam' ? 'matkę' : 'ojca'} do hodowli.
              Pełny profil możesz uzupełnić później.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label>Imię *</Label>
              <Input
                data-testid="quick-add-parent-name"
                value={quickAddForm.name}
                onChange={(e) => setQuickAddForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder={quickAddModal.type === 'dam' ? 'np. Bella' : 'np. Max'}
                className="h-12"
                autoFocus
              />
            </div>
            
            {/* Breed */}
            <div>
              <Label>Rasa *</Label>
              <BreedCombobox
                breeds={breeds}
                value={quickAddForm.breed}
                onChange={(v) => setQuickAddForm(prev => ({ ...prev, breed: v }))}
                hasError={false}
              />
            </div>
            
            {/* Info */}
            <div className="p-3 bg-zinc-50 dark:bg-gray-800 rounded-lg text-sm text-zinc-600 dark:text-zinc-400">
              <p>• {quickAddModal.type === 'dam' ? 'Matka' : 'Ojciec'} zostanie dodany do hodowli <strong>{kennel?.name}</strong></p>
              <p>• Możesz później uzupełnić zdjęcia, tytuły i badania</p>
              <p>• Profil zostanie automatycznie powiązany</p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={closeQuickAddModal}
              disabled={quickAddLoading}
            >
              Anuluj
            </Button>
            <Button
              type="button"
              onClick={handleQuickAddSubmit}
              disabled={quickAddLoading || !quickAddForm.name || !quickAddForm.breed}
              className="bg-[#F47A3B] hover:bg-[#E07A0F]"
            >
              {quickAddLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Dodaję...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Dodaj i wybierz
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </StudioShell>
  );
}
